// Stepper Model - Valtio-based reactive number input with increment/decrement
import {proxy, ref} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";

interface StepperConfig {
    initialValue?: number;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
}

interface StepperEvents {
    change: (value: number) => void;
    increment: (value: number) => void;
    decrement: (value: number) => void;
}

class StepperClass {
    // Reactive state
    value: number;
    disabled: boolean;
    max: number; // Made mutable for dynamic limits

    // Config
    readonly initialValue: number;
    readonly min: number;
    readonly step: number;
    readonly events: EventEmitter<StepperEvents>;

    constructor(config: StepperConfig = {}) {
        this.min = config.min ?? -Infinity;
        this.max = config.max ?? Infinity;
        this.step = config.step ?? 1;
        this.disabled = config.disabled ?? false;

        // Clamp initial value to min/max bounds
        const rawInitialValue = config.initialValue ?? 0;
        this.initialValue = Math.max(this.min, Math.min(this.max, rawInitialValue));
        this.value = this.initialValue;

        this.events = ref(new EventEmitter<StepperEvents>()) as EventEmitter<StepperEvents>;
    }

    // Computed getters
    get canIncrement(): boolean {
        return !this.disabled && this.value + this.step <= this.max;
    }

    get canDecrement(): boolean {
        return !this.disabled && this.value - this.step >= this.min;
    }

    // Actions
    setValue(value: number, silent = false, skipClamp = false): void {
        const newValue = skipClamp ? value : Math.max(this.min, Math.min(this.max, value));
        if (this.value !== newValue) {
            this.value = newValue;
            if (!silent) this.events.emit("change", this.value);
        }
    }

    increment(): void {
        if (this.canIncrement) {
            this.value += this.step;
            this.events.emit("increment", this.value);
            this.events.emit("change", this.value);
        }
    }

    decrement(): void {
        if (this.canDecrement) {
            this.value -= this.step;
            this.events.emit("decrement", this.value);
            this.events.emit("change", this.value);
        }
    }

    setDisabled(disabled: boolean): void {
        this.disabled = disabled;
    }

    setMax(max: number): void {
        this.max = max;
    }

    setMin(min: number): void {
        (this as {min: number}).min = min;
    }

    enable(): void {
        this.disabled = false;
    }

    disable(): void {
        this.disabled = true;
    }

    reset(value?: number): void {
        this.value = value ?? this.initialValue;
        this.events.emit("change", this.value);
    }
}

// Export type
export type StepperInstance = StepperClass;

// Factory function (SSR compatible)
export function Stepper(config?: StepperConfig): StepperInstance {
    return proxy<StepperInstance>(new StepperClass(config));
}
